import { useEffect, useState } from 'react';
import {
  Plus,
  Trash2,
  Pencil,
  Sparkles,
  Loader2,
  Calendar,
} from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';

interface FoundersOffer {
  id: string;
  course_id: string | null;
  plan_id: string | null;
  course_name: string | null;
  plan_name: string | null;
  original_price: number;
  founders_price: number;
  max_slots: number;
  slots_used: number;
  starts_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface OfferFormData {
  course_id: string;
  plan_id: string;
  original_price: string;
  founders_price: string;
  max_slots: string;
  starts_at: string;
  expires_at: string;
}

interface SelectOption {
  id: string;
  name: string;
}

const INITIAL_FORM: OfferFormData = {
  course_id: '',
  plan_id: '',
  original_price: '',
  founders_price: '',
  max_slots: '',
  starts_at: '',
  expires_at: '',
};

function getOfferStatus(offer: FoundersOffer): { label: string; variant: 'green' | 'yellow' | 'red' | 'gray' } {
  if (!offer.is_active) return { label: 'Inactive', variant: 'gray' };
  if (offer.slots_used >= offer.max_slots) return { label: 'Sold Out', variant: 'red' };
  if (offer.expires_at && new Date(offer.expires_at) < new Date()) return { label: 'Expired', variant: 'yellow' };
  if (offer.starts_at && new Date(offer.starts_at) > new Date()) return { label: 'Scheduled', variant: 'yellow' };
  return { label: 'Active', variant: 'green' };
}

function formatDateInput(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().slice(0, 16);
}

const FoundersPricing = () => {
  const [offers, setOffers] = useState<FoundersOffer[]>([]);
  const [courses, setCourses] = useState<SelectOption[]>([]);
  const [plans, setPlans] = useState<SelectOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form modal
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formData, setFormData] = useState<OfferFormData>(INITIAL_FORM);
  const [editingOfferId, setEditingOfferId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Delete
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingOfferId, setDeletingOfferId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    try {
      setIsLoading(true);
      setError(null);
      const [offersRes, coursesRes, plansRes] = await Promise.all([
        api.get<{ offers: FoundersOffer[] }>('/admin/founders-pricing'),
        api.get<{ courses: SelectOption[] }>('/admin/courses?limit=100'),
        api.get<{ plans: SelectOption[] }>('/admin/subscription-plans?limit=100'),
      ]);
      setOffers(offersRes.offers);
      setCourses(coursesRes.courses);
      setPlans(plansRes.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load founders pricing');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreateModal() {
    setFormData(INITIAL_FORM);
    setEditingOfferId(null);
    setFormModalOpen(true);
  }

  function openEditModal(offer: FoundersOffer) {
    setFormData({
      course_id: offer.course_id || '',
      plan_id: offer.plan_id || '',
      original_price: offer.original_price.toString(),
      founders_price: offer.founders_price.toString(),
      max_slots: offer.max_slots.toString(),
      starts_at: formatDateInput(offer.starts_at),
      expires_at: formatDateInput(offer.expires_at),
    });
    setEditingOfferId(offer.id);
    setFormModalOpen(true);
  }

  async function handleSave() {
    if (!formData.course_id && !formData.plan_id) {
      useUIStore.getState().addToast('Select a course or subscription plan', 'error');
      return;
    }
    if (!formData.founders_price || !formData.max_slots) {
      useUIStore.getState().addToast('Founders price and max slots are required', 'error');
      return;
    }

    try {
      setIsSaving(true);
      const payload = {
        course_id: formData.course_id || null,
        plan_id: formData.plan_id || null,
        original_price: parseFloat(formData.original_price) || 0,
        founders_price: parseFloat(formData.founders_price),
        max_slots: parseInt(formData.max_slots),
        starts_at: formData.starts_at ? new Date(formData.starts_at).toISOString() : null,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null,
      };

      if (editingOfferId) {
        await api.put(`/admin/founders-pricing/${editingOfferId}`, payload);
        useUIStore.getState().addToast('Offer updated successfully', 'success');
      } else {
        await api.post('/admin/founders-pricing', payload);
        useUIStore.getState().addToast('Offer created successfully', 'success');
      }

      setFormModalOpen(false);
      fetchAll();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to save offer', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  function openDeleteModal(offerId: string) {
    setDeletingOfferId(offerId);
    setDeleteModalOpen(true);
  }

  async function handleDelete() {
    if (!deletingOfferId) return;

    try {
      setIsDeleting(true);
      await api.delete(`/admin/founders-pricing/${deletingOfferId}`);
      useUIStore.getState().addToast('Offer deleted', 'success');
      setDeleteModalOpen(false);
      setDeletingOfferId(null);
      fetchAll();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to delete offer', 'error');
    } finally {
      setIsDeleting(false);
    }
  }

  async function toggleActive(offer: FoundersOffer) {
    try {
      await api.put(`/admin/founders-pricing/${offer.id}`, { is_active: !offer.is_active });
      setOffers((prev) =>
        prev.map((o) => (o.id === offer.id ? { ...o, is_active: !o.is_active } : o))
      );
      useUIStore.getState().addToast(`Offer ${offer.is_active ? 'deactivated' : 'activated'}`, 'success');
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to update offer', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Founders Pricing</h1>
          <p className="text-gray-600 mt-1">Manage limited-time founders offers</p>
        </div>
        <Button variant="primary" size="md" onClick={openCreateModal}>
          <Plus className="w-5 h-5 mr-2" />
          Add Offer
        </Button>
      </div>

      {error && (
        <Card padding="md">
          <p className="text-red-600">{error}</p>
        </Card>
      )}

      {/* Offers Table */}
      <Card padding="none">
        {offers.length === 0 ? (
          <div className="text-center py-16">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No founders offers yet</h3>
            <p className="text-gray-600 mb-6">Create exclusive early-bird pricing for your courses and plans.</p>
            <Button variant="primary" size="md" onClick={openCreateModal}>
              <Plus className="w-5 h-5 mr-2" />
              Add Offer
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-700">Course / Plan</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Original Price</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Founders Price</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Slots</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Status</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-700">Active</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {offers.map((offer) => {
                  const status = getOfferStatus(offer);
                  const discount = offer.original_price > 0
                    ? Math.round((1 - offer.founders_price / offer.original_price) * 100)
                    : 0;

                  return (
                    <tr key={offer.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {offer.course_name || offer.plan_name || '--'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {offer.course_id ? 'Course' : 'Plan'}
                          {offer.starts_at && (
                            <span className="ml-2">
                              <Calendar className="w-3 h-3 inline mr-0.5" />
                              {new Date(offer.starts_at).toLocaleDateString()}
                              {offer.expires_at && ` - ${new Date(offer.expires_at).toLocaleDateString()}`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500 line-through">
                        {formatCurrency(offer.original_price)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-semibold text-green-700">
                          {formatCurrency(offer.founders_price)}
                        </span>
                        {discount > 0 && (
                          <span className="text-xs text-green-600 ml-1">-{discount}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-medium text-gray-900">{offer.slots_used}</span>
                        <span className="text-gray-400"> / {offer.max_slots}</span>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                          <div
                            className={`h-1.5 rounded-full ${
                              offer.slots_used >= offer.max_slots ? 'bg-red-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min((offer.slots_used / offer.max_slots) * 100, 100)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleActive(offer)}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                            offer.is_active ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out ${
                              offer.is_active ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(offer)}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openDeleteModal(offer.id)}>
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        title={editingOfferId ? 'Edit Founders Offer' : 'New Founders Offer'}
      >
        <div className="space-y-4">
          <Select
            label="Course"
            value={formData.course_id}
            onChange={(e) => setFormData((f) => ({ ...f, course_id: e.target.value, plan_id: e.target.value ? '' : f.plan_id }))}
            placeholder="Select a course"
            options={[
              { label: 'None', value: '' },
              ...courses.map((c) => ({ label: c.name, value: c.id })),
            ]}
          />
          <Select
            label="Subscription Plan"
            value={formData.plan_id}
            onChange={(e) => setFormData((f) => ({ ...f, plan_id: e.target.value, course_id: e.target.value ? '' : f.course_id }))}
            placeholder="Select a plan"
            options={[
              { label: 'None', value: '' },
              ...plans.map((p) => ({ label: p.name, value: p.id })),
            ]}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Original Price"
              type="number"
              value={formData.original_price}
              onChange={(e) => setFormData((f) => ({ ...f, original_price: e.target.value }))}
              placeholder="0.00"
            />
            <Input
              label="Founders Price"
              type="number"
              value={formData.founders_price}
              onChange={(e) => setFormData((f) => ({ ...f, founders_price: e.target.value }))}
              placeholder="0.00"
            />
          </div>
          <Input
            label="Max Slots"
            type="number"
            value={formData.max_slots}
            onChange={(e) => setFormData((f) => ({ ...f, max_slots: e.target.value }))}
            placeholder="100"
            helperText="Maximum number of founders who can claim this offer"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Starts At"
              type="datetime-local"
              value={formData.starts_at}
              onChange={(e) => setFormData((f) => ({ ...f, starts_at: e.target.value }))}
            />
            <Input
              label="Expires At"
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData((f) => ({ ...f, expires_at: e.target.value }))}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="secondary" size="sm" onClick={() => setFormModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isSaving ? 'Saving...' : editingOfferId ? 'Update Offer' : 'Create Offer'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Offer">
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete this founders pricing offer? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" size="sm" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" size="sm" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isDeleting ? 'Deleting...' : 'Delete Offer'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FoundersPricing;
